import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { SectionBarBannerDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { setUrlQueryString } from '@diritto/common/utils/urls';
import styled from '@emotion/styled';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { RoundArrowIcon } from 'icons/RoundArrowIcon';
import React, { useCallback, useMemo } from 'react';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { ZIndex } from 'constants/zIndex';

export const SectionBarBanner = ({
  section,
  trackerExtraInfo,
}: { section: SectionBarBannerDto } & TrackerExtraInfoProps) => {
  const { profile } = useAuthState();
  const { tracker } = useTrackingState();

  const handleClick = useCallback(() => {
    tracker.track(TrackingEventType.ClickMoreLinkOnHomeSection, {
      sectionId: section.id,
      title: section.title,
      trackerExtraInfo,
    });
  }, [section.id, section.title, tracker, trackerExtraInfo]);

  const url = useMemo(
    () =>
      section.moreLink.includes('typeform.com')
        ? setUrlQueryString(section.moreLink, 'profile_id', profile?.id ? profile.id.toString() : '')
        : section.moreLink,
    [profile?.id, section.moreLink],
  );

  const component = useMemo(
    () => (
      <StyledWrapper>
        {section.iconFile && (
          <StyledBlurImageWrapper>
            <IkImage
              fullWidth
              transform={{
                path: section.iconFile.path,
                isPrivate: section.iconFile.isPrivate,
                transformation: ImageTransformOptions[ImageTransformType.Icon],
              }}
            />
          </StyledBlurImageWrapper>
        )}
        <StyledContainer>
          {section.iconFile && (
            <StyledIcon>
              <IkImage
                transform={{
                  path: section.iconFile.path,
                  isPrivate: section.iconFile.isPrivate,
                  transformation: ImageTransformOptions[ImageTransformType.Icon],
                }}
              />
            </StyledIcon>
          )}
          <StyledTitle>{section.title}</StyledTitle>
        </StyledContainer>
        <StyledArrowButton>
          <RoundArrowIcon className="size-[24px]" />
        </StyledArrowButton>
      </StyledWrapper>
    ),
    [section.iconFile, section.title],
  );

  return section.isExternal ? (
    <ExternalLink href={url} onClick={handleClick}>
      {component}
    </ExternalLink>
  ) : (
    <Link href={url} onClick={handleClick}>
      {component}
    </Link>
  );
};

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin: 0 16px;
  background: ${Color.Black};
  border-radius: 2000px;
  overflow: hidden;
`;

const StyledBlurImageWrapper = styled.div`
  position: absolute;

  width: 120%;
  aspect-ratio: 1;
  left: -10%;

  filter: blur(50px);
  transform: translate3d(0, 0, 0);
  opacity: 0.7;
`;

const StyledIcon = styled.div`
  height: 32px;
  width: 32px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: ${ZIndex.SectionBannerBody};
`;

const StyledTitle = styled.div`
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};
  color: ${Color.White};
`;

const StyledArrowButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: ${ZIndex.SectionBannerBody};
`;
